/**
 * SortableTable — generic sortable + selectable table (PR F · §F.1.2).
 *
 * The lens families render rows of findings / patterns / heatmap cells
 * inside this. Sort/filter logic lives in `state/sortableTable.ts` so
 * this layer stays presentational. Headers are buttons (a11y); the
 * active sort is announced via `aria-sort`. Empty bodies render the
 * header row only — the parent DataVizCard owns the empty-state
 * messaging.
 *
 * Virtualisation deliberately deferred — atlas's tables top out around
 * 50–200 rows (lint findings, patterns, refactor proposals). When a
 * future caller hits >1k rows we'll layer it in via react-virtual.
 */
import type { JSX, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { sortRows, type SortDirection } from '../state/sortableTable.js';

export interface SortableColumn<Row> {
  /** Stable key — used to track which column is sorted. */
  key: string;
  /** Header label (text-only; complex headers should pass `headerNode`). */
  label: string;
  /** Pulls the sortable value out of a row. */
  accessor: (row: Row) => string | number;
  /** Custom row renderer — defaults to `String(accessor(row))`. */
  render?: (row: Row) => ReactNode;
  /** Optional custom comparator (delegated to sortableTable.sortRows). */
  sortFn?: (a: Row, b: Row) => number;
  /** Cell text alignment. */
  align?: 'left' | 'right' | 'center';
  /** Render the cell in mono font (numeric / IDs). */
  mono?: boolean;
}

export interface SortSpec {
  key: string;
  dir: SortDirection;
}

export interface SortableTableProps<Row> {
  rows: readonly Row[];
  columns: readonly SortableColumn<Row>[];
  rowKey: (row: Row) => string;
  /** Initial sort; if absent, rows render in input order. */
  initialSort?: SortSpec;
  /** Density knob; consumed via `data-density` for CSS. */
  density?: 'compact' | 'comfortable';
  /** Highlights the row whose key matches. */
  selectedKey?: string;
  /** Fired on row click; passes the row. */
  onRowClick?: (row: Row) => void;
}

export function SortableTable<Row>({
  rows,
  columns,
  rowKey,
  initialSort,
  density = 'comfortable',
  selectedKey,
  onRowClick,
}: SortableTableProps<Row>): JSX.Element {
  const [sort, setSort] = useState<SortSpec | undefined>(initialSort);

  const sortedRows = useMemo(() => {
    if (!sort) return [...rows];
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return [...rows];
    return sortRows(rows, {
      key: col.key,
      accessor: col.accessor,
      ...(col.sortFn ? { sortFn: col.sortFn } : {}),
    }, sort.dir);
  }, [rows, columns, sort]);

  const onHeaderClick = (key: string): void => {
    setSort((cur) => {
      if (!cur || cur.key !== key) return { key, dir: 'asc' };
      return { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  return (
    <table className="sortable-table" data-density={density}>
      <thead>
        <tr>
          {columns.map((col) => {
            const isActive = sort?.key === col.key;
            const ariaSort: 'ascending' | 'descending' | 'none' = isActive
              ? sort!.dir === 'asc' ? 'ascending' : 'descending'
              : 'none';
            return (
              <th
                key={col.key}
                scope="col"
                aria-sort={ariaSort}
                onClick={() => onHeaderClick(col.key)}
                className={`sortable-header${isActive ? ' is-active' : ''}`}
                style={col.align ? { textAlign: col.align } : undefined}
              >
                <button type="button" className="sortable-header-btn">
                  {col.label}
                  {isActive && (
                    <span className="sortable-header-arrow" aria-hidden="true">
                      {sort!.dir === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </button>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((row) => {
          const key = rowKey(row);
          const selected = key === selectedKey;
          const interactive = Boolean(onRowClick);
          // R1.H2 — Keyboard activation via tabIndex + Enter/Space. We
          // intentionally do NOT add role="button": rows here often
          // contain real interactive children (EntityChip buttons),
          // which would trigger axe-core's nested-interactive rule. The
          // implicit <tr> role plus tabIndex is enough for keyboard
          // operability, and the inner buttons remain the canonical
          // semantic affordance for screen readers.
          return (
            <tr
              key={key}
              {...(selected ? { 'aria-selected': true } : {})}
              className={selected ? 'is-selected' : undefined}
              onClick={interactive ? () => onRowClick!(row) : undefined}
              style={interactive ? { cursor: 'pointer' } : undefined}
              tabIndex={interactive ? 0 : undefined}
              onKeyDown={interactive ? (e) => {
                // Only act when the row itself is the keyboard target —
                // otherwise Enter/Space inside a nested button would
                // double-fire (button activation + row activation).
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRowClick!(row);
                }
              } : undefined}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={col.mono ? 'mono' : undefined}
                  style={col.align ? { textAlign: col.align } : undefined}
                >
                  {col.render ? col.render(row) : String(col.accessor(row))}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
